import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MERGE_FIELD_LABELS,
  MERGE_FIELD_TOKENS,
  type PortalContentForKey,
  type PortalContentKey,
  type PortalLevelUpContent,
  type PortalSupportLinksContent,
  type PortalWelcomeContent,
} from "@colab/shared";

// SPA port of /app/admin/portal-content/_components/PortalContentFormFields.tsx.
// Per-key field renderers; parent dialog owns the editable state in a
// single useState.

export function WelcomeFields({
  value,
  onChange,
}: {
  value: PortalWelcomeContent;
  onChange: (next: PortalWelcomeContent) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="welcome-title">Title</Label>
        <Input
          id="welcome-title"
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          placeholder="Welcome back, {partner.first_name} 👋"
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Merge tokens are substituted at view time. Insert one of:{" "}
          {MERGE_FIELD_TOKENS.map((t) => (
            <code key={t} className="ml-1 rounded bg-muted px-1 py-0.5">
              {`{${t}}`}
            </code>
          ))}
        </p>
      </div>

      <div>
        <Label htmlFor="welcome-body">Body</Label>
        <Textarea
          id="welcome-body"
          rows={3}
          value={value.body}
          onChange={(e) => onChange({ ...value, body: e.target.value })}
          placeholder="Your partner hub — register deals, browse resources, and track progress."
        />
      </div>

      <div>
        <Label htmlFor="welcome-cover">Cover image URL (optional)</Label>
        <Input
          id="welcome-cover"
          value={value.cover_image_url ?? ""}
          onChange={(e) =>
            onChange({ ...value, cover_image_url: e.target.value || null })
          }
          placeholder="https://…"
        />
      </div>

      <CtaFields
        label="Primary CTA"
        cta={value.primary_cta ?? null}
        onChange={(cta) => onChange({ ...value, primary_cta: cta })}
      />
      <CtaFields
        label="Secondary CTA"
        cta={value.secondary_cta ?? null}
        onChange={(cta) => onChange({ ...value, secondary_cta: cta })}
      />
    </div>
  );
}

function CtaFields({
  label,
  cta,
  onChange,
}: {
  label: string;
  cta: { label: string; href: string } | null;
  onChange: (next: { label: string; href: string } | null) => void;
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {cta ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            className="h-7 text-xs"
          >
            Remove
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange({ label: "", href: "" })}
            className="h-7 text-xs"
          >
            <Plus className="mr-1 h-3 w-3" /> Add
          </Button>
        )}
      </div>
      {cta ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            value={cta.label}
            onChange={(e) => onChange({ ...cta, label: e.target.value })}
            placeholder="Register a deal"
          />
          <Input
            value={cta.href}
            onChange={(e) => onChange({ ...cta, href: e.target.value })}
            placeholder="/portal/deals/new"
          />
        </div>
      ) : null}
    </div>
  );
}

export function LevelUpFields({
  value,
  onChange,
}: {
  value: PortalLevelUpContent;
  onChange: (next: PortalLevelUpContent) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="lvl-heading">Heading</Label>
        <Input
          id="lvl-heading"
          value={value.heading}
          onChange={(e) => onChange({ ...value, heading: e.target.value })}
          placeholder="Level up your partnership"
        />
      </div>
      <div>
        <Label htmlFor="lvl-sub">Subheading (optional)</Label>
        <Textarea
          id="lvl-sub"
          rows={2}
          value={value.subheading ?? ""}
          onChange={(e) => onChange({ ...value, subheading: e.target.value })}
          placeholder="Move through tiers as you bring more revenue to Bookboost."
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label htmlFor="lvl-cta-label">CTA label (optional)</Label>
          <Input
            id="lvl-cta-label"
            value={value.cta_label ?? ""}
            onChange={(e) => onChange({ ...value, cta_label: e.target.value })}
            placeholder="See benefits"
          />
        </div>
        <div>
          <Label htmlFor="lvl-cta-href">CTA href (optional)</Label>
          <Input
            id="lvl-cta-href"
            value={value.cta_href ?? ""}
            onChange={(e) => onChange({ ...value, cta_href: e.target.value })}
            placeholder="/portal/resources"
          />
        </div>
      </div>
    </div>
  );
}

export function SupportLinksFields({
  value,
  onChange,
}: {
  value: PortalSupportLinksContent;
  onChange: (next: PortalSupportLinksContent) => void;
}) {
  function updateLink(
    idx: number,
    patch: Partial<PortalSupportLinksContent["links"][number]>,
  ) {
    const next = value.links.map((link, i) =>
      i === idx ? { ...link, ...patch } : link,
    );
    onChange({ ...value, links: next });
  }
  function removeLink(idx: number) {
    onChange({ ...value, links: value.links.filter((_, i) => i !== idx) });
  }
  function addLink() {
    onChange({
      ...value,
      links: [...value.links, { label: "", href: "", description: "" }],
    });
  }

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="sup-heading">Heading</Label>
        <Input
          id="sup-heading"
          value={value.heading}
          onChange={(e) => onChange({ ...value, heading: e.target.value })}
          placeholder="Need help?"
        />
      </div>

      <div className="space-y-2">
        <Label>Links</Label>
        {value.links.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No links yet. Add one to populate the Support page.
          </p>
        ) : (
          <ul className="space-y-2">
            {value.links.map((link, idx) => (
              <li key={idx} className="rounded-md border bg-muted/20 p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    value={link.label}
                    onChange={(e) => updateLink(idx, { label: e.target.value })}
                    placeholder="Label"
                  />
                  <Input
                    value={link.href}
                    onChange={(e) => updateLink(idx, { href: e.target.value })}
                    placeholder="/portal/resources"
                  />
                </div>
                <Input
                  value={link.description ?? ""}
                  onChange={(e) =>
                    updateLink(idx, { description: e.target.value })
                  }
                  placeholder="Optional short description"
                  className="mt-2"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLink(idx)}
                    className="h-7 text-xs"
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addLink}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add link
        </Button>
      </div>
    </div>
  );
}

export function emptyContentForKey<K extends PortalContentKey>(
  key: K,
): PortalContentForKey[K] {
  let out:
    | PortalWelcomeContent
    | PortalLevelUpContent
    | PortalSupportLinksContent;
  switch (key) {
    case "welcome":
      out = {
        title: "Welcome back, {partner.first_name} 👋",
        body: "",
        cover_image_url: null,
        primary_cta: null,
        secondary_cta: null,
      } satisfies PortalWelcomeContent;
      break;
    case "level_up":
      out = { heading: "Level up your partnership" } satisfies PortalLevelUpContent;
      break;
    case "support_links":
      out = { heading: "Need help?", links: [] } satisfies PortalSupportLinksContent;
      break;
    default: {
      const _exhaustive: never = key;
      void _exhaustive;
      throw new Error(`Unsupported portal_content key: ${String(key)}`);
    }
  }
  return out as unknown as PortalContentForKey[K];
}

export const MERGE_TOKEN_HINT = MERGE_FIELD_TOKENS.map(
  (t) => `${t} (${MERGE_FIELD_LABELS[t]})`,
).join(" · ");
